/**
 * TYPES
 */
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {_aggregates} from 'src/aggregates/index.d';
import {ITimelineAdded} from 'src/aggregates/timelines/events.d';
import {ITimelineAllContactMessagesLoaded}
    from 'src/aggregates/timelines/events.d';
import {ITimelineByConversationCleared}
    from 'src/aggregates/timelines/events.d';
import {ITimelineByConversationLoaded} from 'src/aggregates/timelines/events.d';
import {ITimelineByConversationMessagesLoaded}
    from 'src/aggregates/timelines/events.d';
import {ITimelineByConversationMessagesLoading}
    from 'src/aggregates/timelines/events.d';
import {ITimelineByConversationMessagesNotLoaded}
    from 'src/aggregates/timelines/events.d';
import {ITimelineByConversationRemoved}
    from 'src/aggregates/timelines/events.d';
import {ITimelineErrorReset} from 'src/aggregates/timelines/events.d';
import {ITimelineFocused} from 'src/aggregates/timelines/events.d';
import {ITimelineLoaded} from 'src/aggregates/timelines/events.d';
import {ITimelineLoading} from 'src/aggregates/timelines/events.d';
import {ITimelineMessageAckReceived} from 'src/aggregates/timelines/events.d';
import {ITimelineMessageByOriginalIdFetched}
    from 'src/aggregates/timelines/events.d';
import {ITimelineMessageByOriginalIdFetching}
    from 'src/aggregates/timelines/events.d';
import {ITimelineMessageByOriginalIdNotFetched}
    from 'src/aggregates/timelines/events.d';
import {ITimelineMessageOriginalIdReceived}
    from 'src/aggregates/timelines/events.d';
import {ITimelineMessagesLoaded} from 'src/aggregates/timelines/events.d';
import {ITimelineMessagesLoading} from 'src/aggregates/timelines/events.d';
import {ITimelineMessagesNotLoaded} from 'src/aggregates/timelines/events.d';
import {ITimelineNotLoaded} from 'src/aggregates/timelines/events.d';
import {ITimelineRemoved} from 'src/aggregates/timelines/events.d';
import {ITimelineTemplateSending} from 'src/aggregates/timelines/events.d';
import {ITimelineTemplateSent} from 'src/aggregates/timelines/events.d';
import {ITimelineTemplateNotSent} from 'src/aggregates/timelines/events.d';
import {types} from 'src/aggregates/timelines/events.d';
import {ITimeline} from 'src/aggregates/timelines/handlers.d';
import {IMessage} from 'src/aggregates/timelines/state.d';


/**
 * CODE
 */

/**
 * I create a timeline added event.
 *
 * :param account: account id
 * :param application: conversation current application uid
 * :param end: conversation end timestamp
 * :param id: conversation id
 * :param lastInteraction: conversation last interaction timestamp
 * :param remote: contact uid
 * :param start: conversation start timestamp
 * :param status: conversation exit status
 * :param subject: conversation subject
 *
 * :returns: timeline added event
 */
function timelineAdded (
    account: number,
    application: string | null,
    end: number | null,
    id: number,
    lastInteraction: number,
    remote: string,
    start: number,
    status: number | null,
    subject: string | null,
): ITimelineAdded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_ADDED,
        _target: null,
        account,
        application,
        end,
        id,
        lastInteraction,
        remote,
        start,
        status,
        subject,
        type: types.TIMELINE_ADDED,
    };
}


/**
 * I create a timeline all contact messages loaded event.
 *
 * :returns: timeline all contact messages loaded event
 */
function timelineAllContactMessagesLoaded (): ITimelineAllContactMessagesLoaded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_ALL_CONTACT_MESSAGES_LOADED,
        _target: null,
        type: types.TIMELINE_ALL_CONTACT_MESSAGES_LOADED,
    };
}


/**
 * I create a timeline by conversation cleared event.
 *
 * :returns: timeline by conversation cleared event
 */
function timelineByConversationCleared(): ITimelineByConversationCleared
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_BY_CONVERSATION_CLEARED,
        _target: null,
        type: types.TIMELINE_BY_CONVERSATION_CLEARED,
    };
}


/**
 * I create a timeline by conversation loaded event.
 *
 * :param conversation: conversation id
 * :param timeline: timeline events
 *
 * :returns: timeline by conversation loaded event
 */
function timelineByConversationLoaded(
    conversation: number,
    timeline: ITimeline[],
): ITimelineByConversationLoaded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_BY_CONVERSATION_LOADED,
        _target: null,
        conversation,
        timeline,
        type: types.TIMELINE_BY_CONVERSATION_LOADED,
    };
}


/**
 * I create a timeline by conversations messages loaded event.
 *
 * :param conversation: conversation id
 * :param messages: timeline messages
 * :param timeline: timeline conversation id
 *
 * :returns: timeline by conversations messages loaded event
 */
function timelineByConversationMessagesLoaded (
    conversation: number,
    messages: IMessage[],
    timeline: number,
): ITimelineByConversationMessagesLoaded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_BY_CONVERSATION_MESSAGES_LOADED,
        _target: null,
        conversation,
        messages,
        timeline,
        type: types.TIMELINE_BY_CONVERSATION_MESSAGES_LOADED,
    };
}


/**
* I create a timeline by conversations messages loading event.
*
* :param conversation: conversation id
* :param timeline: timeline conversation id
*
* :returns: timeline by conversations messages loading event
*/
function timelineByConversationMessagesLoading (
    conversation: number,
    timeline: number,
): ITimelineByConversationMessagesLoading
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_BY_CONVERSATION_MESSAGES_LOADING,
        _target: null,
        conversation,
        timeline,
        type: types.TIMELINE_BY_CONVERSATION_MESSAGES_LOADING,
    };
}


/**
* I create a timeline by conversations messages not loaded event.
*
* :param conversation: conversation id
* :param timeline: timeline conversation id
*
* :returns: timeline by conversations messages not loaded event
*/
function timelineByConversationMessagesNotLoaded (
    conversation: number,
    timeline: number,
): ITimelineByConversationMessagesNotLoaded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_BY_CONVERSATION_MESSAGES_NOT_LOADED,
        _target: null,
        conversation,
        timeline,
        type: types.TIMELINE_BY_CONVERSATION_MESSAGES_NOT_LOADED,
    };
}


/**
* I create a timeline by conversation removed event.
*
* :param conversation: conversation id
*
* :returns: timeline by conversation removed event
*/
function timelineByConversationRemoved (
    conversation: number,
): ITimelineByConversationRemoved
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_BY_CONVERSATION_REMOVED,
        _target: null,
        conversation,
        type: types.TIMELINE_BY_CONVERSATION_REMOVED,
    };
}


/**
* I create a timeline error reset event.
*
* :returns: timeline error reset event
*/
function timelineErrorReset (): ITimelineErrorReset
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_ERROR_RESET,
        _target: null,
        type: types.TIMELINE_ERROR_RESET,
    };
}


/**
 * I create a timeline focused event.
 *
 * :param id: conversation id to be focused
 *
 * :returns: timeline focused event.
 */
function timelineFocused (id: number): ITimelineFocused
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_FOCUSED,
        _target: id,
        type: types.TIMELINE_FOCUSED,
    };
}


/**
 * I create a timeline loaded event.
 *
 * :returns: timeline loaded event
 */
const timelineLoaded = (timeline: ITimeline[]): ITimelineLoaded =>
    ({
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_LOADED,
        _target: null,
        timeline,
        type: types.TIMELINE_LOADED,
    });


/**
 * I create a timeline loading event.
 *
 * :returns: timeline loading event
 */
const timelineLoading = (): ITimelineLoading => ({
    _aggregate: _aggregates.TIMELINES,
    _name: types.TIMELINE_LOADING,
    _target: null,
    type: types.TIMELINE_LOADING,
});


/**
 * I create a timeline conversation message ack received event.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param ack: received ack
 *
 * :returns: timeline conversation message ack received event
 */
function timelineMessageAckReceived (
    conversationId: number,
    messageId: number,
    ack: AckTypes,
): ITimelineMessageAckReceived
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGE_ACK_RECEIVED,
        _target: conversationId,
        ack,
        id: messageId,
        type: types.TIMELINE_MESSAGE_ACK_RECEIVED,
    };
}


/**
 * I create a conversation message by original id fetched event.
 *
 * :param conversation: conversation id
 * :param message: message id
 * :param current: id of current conversation
 * :param data: message data
 *
 * :returns: conversation message by original id fetched event
 */
function timelineMessageByOriginalIdFetched (
    conversation: number,
    message: number,
    current: string | number | null,
    data: IMessage,
): ITimelineMessageByOriginalIdFetched
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGE_BY_ORIGINAL_ID_FETCHED,
        _target: conversation,
        current,
        data,
        message,
        type: types.TIMELINE_MESSAGE_BY_ORIGINAL_ID_FETCHED,
    };
}


/**
 * I create a conversation message by original id fetching event.
 *
 * :param conversation: conversation id of message
 * :param message: message id
 * :param current: id of current conversation
 *
 * :returns: conversation message by original id fetching event
 */
function timelineMessageByOriginalIdFetching (
    conversation: number,
    message: number,
    current: string | number | null,
): ITimelineMessageByOriginalIdFetching
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGE_BY_ORIGINAL_ID_FETCHING,
        _target: conversation,
        current,
        message,
        type: types.TIMELINE_MESSAGE_BY_ORIGINAL_ID_FETCHING,
    };
}


/**
 * I create a conversation message by original id not fetched event.
 *
 * :param conversation: conversation id
 * :param message: message id
 * :param current: id of current conversation
 *
 * :returns: conversation message by original id not fetched event
 */
function timelineMessageByOriginalIdNotFetched (
    conversation: number,
    message: number,
    current: string | number | null,
): ITimelineMessageByOriginalIdNotFetched
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGE_BY_ORIGINAL_ID_NOT_FETCHED,
        _target: conversation,
        current,
        message,
        type: types.TIMELINE_MESSAGE_BY_ORIGINAL_ID_NOT_FETCHED,
    };
}


/**
 * I create a timeline conversation message original id received event.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param originalId: received original id
 *
 * :returns: timeline conversation message original id received event
 */
function timelineMessageOriginalIdReceived (
    conversationId: number,
    messageId: number,
    originalId: string,
): ITimelineMessageOriginalIdReceived
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGE_ORIGINAL_ID_RECEIVED,
        _target: conversationId,
        id: messageId,
        originalId,
        type: types.TIMELINE_MESSAGE_ORIGINAL_ID_RECEIVED,
    };
}


/**
 * I create a timeline messages loaded event.
 *
 * :param id: conversation id
 * :param messages: conversation messages
 *
 * :returns: timeline messages loaded event
 */
function timelineMessagesLoaded (id: number,
                                 messages: IMessage[]): ITimelineMessagesLoaded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGES_LOADED,
        _target: id,
        messages,
        type: types.TIMELINE_MESSAGES_LOADED,
    };
}


/**
 * I create a timeline messages loading event.
 *
 * :param id: conversation id
 *
 * :returns: timeline messages loading event
 */
function timelineMessagesLoading (id: number): ITimelineMessagesLoading
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGES_LOADING,
        _target: id,
        type: types.TIMELINE_MESSAGES_LOADING,
    };
}


/**
 * I create a timeline messages not loaded event.
 *
 * :param id: conversation id
 *
 * :returns: timeline messages not loaded event
 */
function timelineMessagesNotLoaded (id: number): ITimelineMessagesNotLoaded
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: types.TIMELINE_MESSAGES_NOT_LOADED,
        _target: id,
        type: types.TIMELINE_MESSAGES_NOT_LOADED,
    };
}


/**
 * I create a timeline not loaded event.
 *
 * :returns: timeline not loaded event
 */
const timelineNotLoaded = (): ITimelineNotLoaded => ({
    _aggregate: _aggregates.TIMELINES,
    _name: types.TIMELINE_NOT_LOADED,
    _target: null,
    type: types.TIMELINE_NOT_LOADED,
});


/**
 * I create a timeline removed event.
 *
 * :param id: conversation id
 *
 * :returns: timeline removed event
 */
const timelineRemoved = (id: number): ITimelineRemoved => ({
    _aggregate: _aggregates.TIMELINES,
    _name: types.TIMELINE_REMOVED,
    _target: id,
    type: types.TIMELINE_REMOVED,
});


/**
 * I create a timeline template sending event.
 *
 * :param id: conversation id
 * :param payload: template payload content
 *
 * :returns: timeline template sending event
 */
const timelineTemplateSending = (
    id: number,
    payload: IMessage,
): ITimelineTemplateSending => ({
    _aggregate: _aggregates.TIMELINES,
    _name: types.TIMELINE_TEMPLATE_SENDING,
    _target: id,
    payload: payload,
    type: types.TIMELINE_TEMPLATE_SENDING,
});


/**
 * I create a timeline template sent event.
 *
 * :param id: conversation id
 * :param oldOriginalId: old original id value
 * :param payload: template payload received from conversis
 *
 * :returns: timeline template sent event
 */
const timelineTemplateSent = (
    id: number,
    oldOriginalId: string,
    payload: IMessage,
): ITimelineTemplateSent => ({
    _aggregate: _aggregates.TIMELINES,
    _name: types.TIMELINE_TEMPLATE_SENT,
    _target: id,
    oldOriginalId: oldOriginalId,
    payload: payload,
    type: types.TIMELINE_TEMPLATE_SENT,
});


/**
 * I create a timeline template not sent event.
 *
 * :param id: conversation id
 * :param originalId: message original id
 * :returns: timeline template not sent event
 */
const timelineTemplateNotSent = (
    id: number,
    originalId: string,
): ITimelineTemplateNotSent => ({
    _aggregate: _aggregates.TIMELINES,
    _name: types.TIMELINE_TEMPLATE_NOT_SENT,
    _target: id,
    originalId: originalId,
    type: types.TIMELINE_TEMPLATE_NOT_SENT,
});


/**
 * EXPORTS
 */
export {
    timelineAdded,
    timelineAllContactMessagesLoaded,
    timelineByConversationCleared,
    timelineByConversationLoaded,
    timelineByConversationMessagesLoaded,
    timelineByConversationMessagesLoading,
    timelineByConversationMessagesNotLoaded,
    timelineByConversationRemoved,
    timelineErrorReset,
    timelineFocused,
    timelineLoaded,
    timelineLoading,
    timelineMessageAckReceived,
    timelineMessageByOriginalIdFetched,
    timelineMessageByOriginalIdFetching,
    timelineMessageByOriginalIdNotFetched,
    timelineMessageOriginalIdReceived,
    timelineMessagesLoaded,
    timelineMessagesLoading,
    timelineMessagesNotLoaded,
    timelineNotLoaded,
    timelineRemoved,
    timelineTemplateSending,
    timelineTemplateSent,
    timelineTemplateNotSent,
};
